import { useCallback, useEffect, useState } from "react";
import { serviceLabel } from "@/lib/serviceLabel";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, MessageSquare, Navigation, Play, Check, Eye, ChevronLeft, ChevronRight, CalendarDays, RefreshCw, Truck, FileSignature, AlertTriangle } from "lucide-react";
import { adminClient } from "./api";
import RecordDrawer from "./RecordDrawer";
import DeliveryReceiptDialog from "./DeliveryReceiptDialog";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const STATUS = {
  awaiting_payment: "bg-amber-100 text-amber-900", scheduled: "bg-emerald-100 text-emerald-900",
  in_progress: "bg-sky-100 text-sky-900", completed: "bg-zinc-200 text-zinc-700",
};
const shift = (iso, days) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const todayLocal = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());

function JobCard({ job, onStatus, onOpen, onOnMyWay, onReceipt, busy }) {
  const [eta, setEta] = useState(30);
  const tel = `tel:${(job.phone || "").replace(/\D/g, "")}`;
  const sms = `sms:${(job.phone || "").replace(/\D/g, "")}`;
  const maps = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${job.address}, ${job.zip_code}`)}`;
  const done = job.status === "completed";
  return (
    <Card data-testid={`today-job-${job.ref_id}`} className={`p-4 border-2 ${done ? "border-zinc-200 opacity-70" : "border-[#0C2E2B]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-tactical text-[10px] uppercase tracking-[0.25em] text-[#0C2E2B]/60">{job.preferred_window}</div>
          <div className="font-display font-black text-xl text-[#0C2E2B] leading-tight">{serviceLabel(job.service_type)} · {job.name}</div>
          {job.store_flag && !job.store_flag.cleared_at && (
            <div data-testid={`today-not-ready-${job.ref_id}`} className="mt-1 inline-flex items-center gap-1 bg-red-600 text-white text-[11px] font-black uppercase tracking-wider px-2 py-1 rounded">
              <AlertTriangle className="w-3.5 h-3.5" /> Store says NOT READY · {job.store_flag.reason_label}{job.store_flag.note ? ` — ${job.store_flag.note}` : ""}
            </div>
          )}
          <div className="text-sm text-zinc-600 mt-0.5">{job.address}, {job.zip_code}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge data-testid={`today-status-${job.ref_id}`} className={`text-[10px] ${STATUS[job.status] || "bg-zinc-100 text-zinc-800"} hover:${STATUS[job.status] || ""}`}>{String(job.status || "").replace(/_/g, " ")}</Badge>
          <span className="text-[10px] text-zinc-500 font-mono-data">{job.ref_id}</span>
          {job.payment_status !== "paid" && <span className="text-[10px] font-bold text-amber-700">Deposit unpaid</span>}
          {job.money?.total != null && <span className="text-[10px] text-zinc-600">Balance {money(job.money.balance_due)}</span>}
          {job.delivery_receipt && <span data-testid={`today-receipt-badge-${job.ref_id}`} className="text-[10px] font-bold text-emerald-700 flex items-center gap-0.5"><FileSignature className="w-3 h-3" /> Signed by {job.delivery_receipt.signed_by}</span>}
        </div>
      </div>
      {job.notes && <p className="text-xs text-zinc-500 italic mt-2">"{job.notes}"</p>}
      <div className="grid grid-cols-3 gap-2 mt-4">
        <a href={tel} data-testid={`today-call-${job.ref_id}`} className="h-12 rounded-md bg-[#0C2E2B] text-[#FFDF00] font-black text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><Phone className="w-4 h-4" /> Call</a>
        <a href={sms} data-testid={`today-text-${job.ref_id}`} className={`h-12 rounded-md border-2 border-[#0C2E2B] text-[#0C2E2B] font-black text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform ${job.sms_opted_out ? "opacity-40 pointer-events-none" : ""}`}><MessageSquare className="w-4 h-4" /> Text</a>
        <a href={maps} target="_blank" rel="noreferrer" data-testid={`today-navigate-${job.ref_id}`} className="h-12 rounded-md border-2 border-[#0C2E2B] text-[#0C2E2B] font-black text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><Navigation className="w-4 h-4" /> Navigate</a>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        {!done && (
          <div className="col-span-3 flex gap-2" data-testid={`today-omw-${job.ref_id}`}>
            <select data-testid={`today-omw-eta-${job.ref_id}`} value={eta} onChange={(e) => setEta(Number(e.target.value))} className="h-11 rounded-md border-2 border-[#0C2E2B] bg-white px-2 text-xs font-bold text-[#0C2E2B]">
              {[15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>~{m} min</option>)}
            </select>
            <Button data-testid={`today-omw-btn-${job.ref_id}`} disabled={busy || (!job.email && !job.sms_consent)} onClick={() => onOnMyWay(job, eta)} className="flex-1 h-11 rounded-md font-black text-xs bg-[#FFDF00] text-[#0C2E2B] hover:bg-[#E5C900]">
              <Truck className="w-4 h-4 mr-1" /> {job.on_my_way_at ? "On my way (again)" : "On my way"}
            </Button>
          </div>
        )}
        {job.status !== "in_progress" && !done && (
          <Button data-testid={`today-start-${job.ref_id}`} disabled={busy} onClick={() => onStatus(job, "in_progress")} variant="outline" className="h-11 rounded-md font-bold text-xs"><Play className="w-4 h-4 mr-1" /> Start</Button>
        )}
        {!done && (
          <Button data-testid={`today-complete-${job.ref_id}`} disabled={busy} onClick={() => onStatus(job, "completed")} className={`h-11 rounded-md font-black text-xs bg-emerald-600 hover:bg-emerald-700 text-white ${job.status === "in_progress" ? "col-span-2" : ""}`}><Check className="w-4 h-4 mr-1" /> Mark complete</Button>
        )}
        <Button data-testid={`today-details-${job.ref_id}`} onClick={() => onOpen(job)} variant="outline" className={`h-11 rounded-md font-bold text-xs ${done ? "col-span-3" : ""}`}><Eye className="w-4 h-4 mr-1" /> Details</Button>
        <Button data-testid={`today-receipt-${job.ref_id}`} onClick={() => onReceipt(job)} variant="outline" className="col-span-3 h-11 rounded-md font-bold text-xs border-[#0C2E2B] text-[#0C2E2B]"><FileSignature className="w-4 h-4 mr-1" /> {job.delivery_receipt ? "Redo proof of delivery" : "Proof of delivery · photo + signature"}</Button>
      </div>
    </Card>
  );
}

export default function TodayAdmin() {
  const [date, setDate] = useState(todayLocal());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [receiptJob, setReceiptJob] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await adminClient.get(`/admin/today?date=${date}`); setData(data); }
    catch { toast.error("Could not load the day's jobs"); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (job, status) => {
    setBusy(true);
    try {
      await adminClient.put(`/admin/bookings/${job.id}/status`, { status, site_url: window.location.origin });
      toast.success(status === "completed" ? `${job.name} marked complete — review request queued for tomorrow` : `Started ${job.name}`);
      load();
    } catch { toast.error("Update failed"); } finally { setBusy(false); }
  };

  const onMyWay = async (job, eta) => {
    setBusy(true);
    try {
      const { data } = await adminClient.post(`/admin/bookings/${job.id}/on-my-way`, { eta_minutes: eta });
      toast.success(`Told ${job.name} you're ~${eta} min out (${data.sent_via.join(" + ")})${data.skipped?.length ? ` · ${data.skipped[0]}` : ""}`, { duration: 7000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send", { duration: 8000 }); } finally { setBusy(false); }
  };

  const label = new Date(`${date}T12:00:00`).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const isToday = date === todayLocal();

  return (
    <div data-testid="today-admin" className="space-y-5 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0C2E2B] flex items-center gap-2"><CalendarDays className="w-7 h-7" /> {isToday ? "Today" : label}</h1>
          {isToday && <p className="text-sm text-zinc-500">{label}</p>}
        </div>
        <div className="flex items-center gap-1">
          <Button data-testid="today-prev-btn" size="icon" variant="outline" className="h-10 w-10 rounded-md" onClick={() => setDate(shift(date, -1))}><ChevronLeft className="w-4 h-4" /></Button>
          <Button data-testid="today-today-btn" variant="outline" className="h-10 rounded-md text-xs font-bold" onClick={() => setDate(todayLocal())} disabled={isToday}>Today</Button>
          <Button data-testid="today-next-btn" size="icon" variant="outline" className="h-10 w-10 rounded-md" onClick={() => setDate(shift(date, 1))}><ChevronRight className="w-4 h-4" /></Button>
          <Button data-testid="today-refresh-btn" size="icon" variant="ghost" className="h-10 w-10" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="today-counts">
          <Card className="p-3 border-2 border-zinc-200"><div className="font-display font-black text-2xl text-[#0C2E2B]">{data.counts.total}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Jobs</div></Card>
          <Card className="p-3 border-2 border-zinc-200"><div className="font-display font-black text-2xl text-emerald-700">{data.counts.done}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Done</div></Card>
          <Card className="p-3 border-2 border-zinc-200"><div className="font-display font-black text-2xl text-amber-700">{data.counts.unpaid_deposit}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Unpaid deposit</div></Card>
        </div>
      )}

      {!data ? <p className="text-sm text-zinc-500">Loading…</p> : data.jobs.length === 0 ? (
        <Card className="p-10 text-center text-zinc-500 text-sm" data-testid="today-empty">No jobs on the books for this day.</Card>
      ) : (
        <div className="space-y-3">{data.jobs.map((j) => <JobCard key={j.id} job={j} busy={busy} onStatus={setStatus} onOnMyWay={onMyWay} onReceipt={setReceiptJob} onOpen={(job) => setOpenId(job.id)} />)}</div>
      )}
      <RecordDrawer kind="booking" recordId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <DeliveryReceiptDialog job={receiptJob} open={!!receiptJob} onClose={() => setReceiptJob(null)} onSaved={load} />
    </div>
  );
}
